import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { clearSheetHeight, setSheetHeight } from './sheetLift.js';

/**
 * Gesto "arrastrar la manija hacia abajo para cerrar" de las hojas (bottom
 * sheets) de speak.
 *
 * Por qué existe: todas las hojas dibujaban la manija, pero era un ADORNO —
 * prometía un gesto que no existía en ningún lado salvo `OrbitaMoreSheet`, que
 * lo tenía cableado a mano. Este hook es esa implementación generalizada, para
 * que las hojas de toda la app se comporten igual.
 *
 * Cómo se usa:
 *
 *   const { grabProps, sheetStyle } = useSheetDrag(onClose);
 *   <div className="mi-hoja" style={sheetStyle}>
 *     <div className="mi-handle sheet-grab" {...grabProps} />
 *
 * `.sheet-grab` (global.css) es la otra mitad y no es opcional:
 *  - agranda la zona de agarre (la manija dibujada mide 4px de alto —
 *    inenganchable con el pulgar; el `::before` la lleva a ~32×130);
 *  - le saca el eje vertical al browser (`touch-action: none`), sin lo cual en
 *    las hojas que scrollean el gesto se lo come el contenedor.
 *
 * Gotcha del `transform` inline: las hojas abren con una animación CSS
 * (`sheetUp`) declarada con fill-mode `both`, así que ésta sigue pisando el
 * `transform` del elemento DESPUÉS de terminar. Por eso `sheetStyle` apaga la
 * animación (`animation: 'none'`) en cuanto el gesto arranca: sin eso el
 * arrastre no movería nada.
 *
 * La X de cada hoja NO se va: un arrastre no es alcanzable por teclado, mouse
 * ni lector de pantalla, así que la manija es un atajo para el pulgar, no el
 * único camino de salida. Por eso `grabProps` la marca `aria-hidden`.
 *
 * Además PUBLICA el alto de la hoja (`sheetLift`), que es de lo que se cuelga
 * el reproductor de Escuchar para apoyarse en su borde en vez de quedar tapado
 * (card #281). Va acá y no en cada hoja justamente porque este hook es el único
 * lugar por el que pasan todas: la hoja número 24 lo hereda sin enterarse. La
 * hoja es el `parentElement` de la manija — el mismo contrato que ya existía
 * (`.sheet-grab` va en la manija, hija directa de la hoja).
 */

/** Arrastre (px) a partir del cual soltar cierra la hoja. */
const CLOSE_PX = 96;
/** Un flick corto pero rápido (px/ms) también cierra, aunque no llegue a `CLOSE_PX`. */
const CLOSE_VELOCITY = 0.55;
/** Arrastre mínimo para que la velocidad cuente (evita cerrar con un tap tembloroso). */
const FLICK_MIN_PX = 24;
/** Salida: la hoja termina de irse sola hacia abajo antes de desmontarse. */
const EXIT_MS = 180;
/** Vuelta a su lugar cuando el arrastre no alcanzó el umbral. */
const SNAP_MS = 220;

/**
 * `idle` es "nadie la tocó todavía" y `settled` es "volvió a su lugar después
 * de un arrastre". Se distinguen por la animación de apertura: devolverle el
 * `animation` de la hoja después de haberlo apagado no restaura la animación
 * terminada, EMPIEZA UNA NUEVA — la hoja re-hacía su entrada sola cada vez que
 * soltabas el arrastre a mitad de camino. En `settled` queda apagada para
 * siempre (ya abrió; no hay nada que animar).
 */
type Phase = 'idle' | 'drag' | 'snap' | 'settled' | 'exit';

export interface SheetDragProps {
  /** Resuelve la hoja para medirla (ver `sheetLift`). */
  readonly ref: (node: HTMLElement | null) => void;
  readonly onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
  readonly onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  readonly 'aria-hidden': true;
}

export interface SheetDrag {
  /** Va en la manija (junto a la clase `sheet-grab`). */
  readonly grabProps: SheetDragProps;
  /** Va en el `style` de la hoja. `undefined` mientras nadie la arrastra. */
  readonly sheetStyle: CSSProperties | undefined;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useSheetDrag(onClose: () => void): SheetDrag {
  const [dy, setDy] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  // La hoja (padre de la manija) y su alto medido, para `sheetLift`.
  const sheetRef = useRef<HTMLElement | null>(null);
  const rawHeightRef = useRef(0);
  const roRef = useRef<ResizeObserver | null>(null);
  const phaseRef = useRef<Phase>('idle');
  phaseRef.current = phase;
  // Espejo de `dy` para que el handler de "soltar" lea el valor real del gesto
  // y no el del último render.
  const dyRef = useRef(0);
  const startRef = useRef<{ y: number; t: number } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // El cierre por ref: varias hojas pasan un arrow inline (`() => setOpen(false)`)
  // y no queremos re-crear los handlers en cada render por eso.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  /**
   * Publica el alto VISIBLE de la hoja: el medido menos lo que llevás
   * arrastrado, y 0 mientras se está yendo. Así el reproductor la acompaña
   * también cuando la bajás con el dedo.
   */
  const publishHeight = useCallback((): void => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const visible = phaseRef.current === 'exit' ? 0 : rawHeightRef.current - dyRef.current;
    setSheetHeight(sheet, visible);
  }, []);

  /**
   * Ref de la manija: resuelve la hoja y le engancha un `ResizeObserver` (el
   * alto cambia solo — contenido que carga, teclado, cambio de nivel…). jsdom
   * no tiene `ResizeObserver`: ahí se mide una vez y listo, que para los tests
   * (donde todo mide 0) da exactamente lo mismo.
   */
  const grabRef = useCallback(
    (node: HTMLElement | null): void => {
      const previous = sheetRef.current;
      if (previous) {
        clearSheetHeight(previous);
        sheetRef.current = null;
      }
      if (roRef.current) {
        roRef.current.disconnect();
        roRef.current = null;
      }
      const sheet = node?.parentElement ?? null;
      if (!sheet) return;
      sheetRef.current = sheet;
      rawHeightRef.current = sheet.getBoundingClientRect().height;
      publishHeight();
      if (typeof ResizeObserver === 'function') {
        const ro = new ResizeObserver(() => {
          rawHeightRef.current = sheet.getBoundingClientRect().height;
          publishHeight();
        });
        ro.observe(sheet);
        roRef.current = ro;
      }
    },
    [publishHeight],
  );

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLElement>): void => {
    if (e.button !== 0) return;
    const el = e.currentTarget;
    // jsdom no implementa pointer capture; en el browser sí y es lo que hace
    // que los `move`/`up` sigan llegando aunque el dedo se salga de la manija.
    if (typeof el.setPointerCapture === 'function') el.setPointerCapture(e.pointerId);
    startRef.current = { y: e.clientY, t: performance.now() };
    dyRef.current = 0;
    setDy(0);
    setPhase('drag');
  }, []);

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLElement>): void => {
      const start = startRef.current;
      if (start === null) return;
      // Sólo hacia abajo: dejarla subir despegaría la hoja del borde inferior y
      // se vería el fondo por debajo.
      const next = Math.max(0, e.clientY - start.y);
      dyRef.current = next;
      setDy(next);
      publishHeight();
    },
    [publishHeight],
  );

  const finish = useCallback(
    (e: ReactPointerEvent<HTMLElement>): void => {
      const start = startRef.current;
      startRef.current = null;
      if (start === null) return;
      const el = e.currentTarget;
      if (typeof el.releasePointerCapture === 'function' && el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }

      const travelled = dyRef.current;
      const elapsed = Math.max(1, performance.now() - start.t);
      const closes =
        travelled > CLOSE_PX || (travelled > FLICK_MIN_PX && travelled / elapsed > CLOSE_VELOCITY);

      if (closes) {
        // La hoja se va: el reproductor se despide con ella y vuelve a su lugar.
        phaseRef.current = 'exit';
        publishHeight();
        if (prefersReducedMotion()) {
          closeRef.current();
          return;
        }
        setPhase('exit');
        timerRef.current = setTimeout(() => closeRef.current(), EXIT_MS);
        return;
      }

      dyRef.current = 0;
      setDy(0);
      publishHeight();
      setPhase('snap');
      timerRef.current = setTimeout(() => setPhase('settled'), SNAP_MS);
    },
    [publishHeight],
  );

  let sheetStyle: CSSProperties | undefined;
  if (phase === 'drag') {
    sheetStyle = {
      transform: `translateY(${dy}px)`,
      animation: 'none',
      transition: 'none',
      willChange: 'transform',
    };
  } else if (phase === 'snap') {
    sheetStyle = {
      transform: 'translateY(0px)',
      animation: 'none',
      transition: `transform ${SNAP_MS}ms cubic-bezier(0.2, 0.8, 0.2, 1)`,
    };
  } else if (phase === 'settled') {
    sheetStyle = { animation: 'none' };
  } else if (phase === 'exit') {
    sheetStyle = {
      transform: 'translateY(100%)',
      animation: 'none',
      transition: `transform ${EXIT_MS}ms cubic-bezier(0.3, 0, 0.8, 0.3)`,
    };
  }

  return {
    grabProps: {
      ref: grabRef,
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
      'aria-hidden': true,
    },
    sheetStyle,
  };
}
